// Who's signed in, at the foot of the menu: a button that opens a small menu of personal shortcuts and Sign out.
// Keyboard: Enter/Space opens, arrows move, Escape closes (focus goes back to the button).
import { useEffect, useRef, useState, type KeyboardEvent } from "react";
import { Link, useLocation } from "react-router";
import { ROLE_NAME } from "../../lib/labels";
import { Native } from "../../lib/native";
import { logout, useSession } from "../../lib/session";
import { THEME_NAME, getTheme, setTheme, themes, type Theme } from "../../lib/theme";
import { Icon } from "../../ui/Icon";
import { toast } from "../../ui/toast";

const initials = (name: string) => name.split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0].toUpperCase()).join("");

export function AccountMenu({ unread, collapsed }: { unread: number; collapsed: boolean }) {
  const { me } = useSession();
  const [open, setOpen] = useState(false);
  const wrap = useRef<HTMLDivElement>(null);
  const button = useRef<HTMLButtonElement>(null);
  const { pathname } = useLocation();

  useEffect(() => setOpen(false), [pathname]); // navigating from the menu closes it
  useEffect(() => {
    if (!open) return;
    const outside = (e: MouseEvent) => { if (!wrap.current?.contains(e.target as Node)) setOpen(false); };
    document.addEventListener("mousedown", outside);
    wrap.current?.querySelector<HTMLElement>("[role=menuitem]")?.focus();
    return () => document.removeEventListener("mousedown", outside);
  }, [open]);

  const close = () => { setOpen(false); button.current?.focus(); };
  const onKey = (e: KeyboardEvent) => {
    if (e.key === "Escape") { e.stopPropagation(); close(); return; }
    if (e.key !== "ArrowDown" && e.key !== "ArrowUp") return;
    e.preventDefault();
    const items = [...(wrap.current?.querySelectorAll<HTMLElement>("[role=menuitem], [role=menuitemradio]") ?? [])];
    const i = items.indexOf(document.activeElement as HTMLElement);
    items[(i + (e.key === "ArrowDown" ? 1 : -1) + items.length) % items.length]?.focus();
  };
  const [theme, setThemeState] = useState<Theme>(getTheme);
  const pickTheme = (t: Theme) => { setTheme(t); setThemeState(t); };
  const addPerson = () => { setOpen(false); void import("../admin/UsersTab").then((m) => m.openAddPerson()); };

  return (
    <div className="acct" ref={wrap} onKeyDown={onKey}>
      {open && (
        <div className="acct-menu" role="menu" aria-label="Your account">
          <div className="acct-head">
            <span className="acct-av lg" aria-hidden="true">{initials(me.name)}</span>
            <div className="acct-id"><div className="b" title={me.name}>{me.name}</div><div className="small muted" title={me.email}>{me.email}</div></div>
          </div>
          {(me.role === "rep" || me.role === "manager") && <Link role="menuitem" className="acct-item" to="/me"><Icon name="chart" />My numbers</Link>}
          {me.role !== "ops" && <Link role="menuitem" className="acct-item" to="/inbox"><Icon name="bell" />Notifications{unread > 0 && <span className="acct-n">{unread}</span>}</Link>}
          {me.role === "admin" && <>
            <button role="menuitem" className="acct-item" onClick={addPerson}><Icon name="userplus" />Add a person</button>
            <Link role="menuitem" className="acct-item" to="/admin"><Icon name="gear" />Admin settings</Link>
          </>}
          {Native && <>
            <button role="menuitem" className="acct-item" onClick={() => { setOpen(false); Native!.syncNow(); toast("Syncing calls…"); }}><Icon name="sync" />Sync calls now</button>
            <button role="menuitem" className="acct-item" onClick={() => { setOpen(false); Native!.deviceCheck(); }}><Icon name="phone" />Phone check</button>
          </>}
          <div className="acct-sep" role="separator" />
          <div role="group" aria-label="Appearance">
            <div className="acct-label">Appearance</div>
            {themes().map((t) => (
              <button key={t} role="menuitemradio" aria-checked={theme === t} className="acct-item" onClick={() => pickTheme(t)}>
                <span className={"acct-swatch " + t} aria-hidden="true" />{THEME_NAME[t]}
              </button>
            ))}
          </div>
          <div className="acct-sep" role="separator" />
          <button role="menuitem" className="acct-item" onClick={() => void logout()}><Icon name="logout" />Sign out</button>
        </div>
      )}
      <button ref={button} className="acct-btn" aria-haspopup="menu" aria-expanded={open} onClick={() => setOpen((o) => !o)}
        title={collapsed ? `${me.name} · ${ROLE_NAME[me.role]}` : undefined} aria-label={collapsed ? `${me.name}, account menu` : undefined}>
        <span className="acct-av" aria-hidden="true">{initials(me.name)}</span>
        <span className="acct-id"><span className="nm">{me.name}</span><span className="rl">{ROLE_NAME[me.role]}</span></span>
        <Icon name="updown" />
      </button>
    </div>
  );
}
